import { useCallback, useEffect, useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useFleetSocket } from "@/hooks/useFleetSocket";
import { sinceLabel } from "@/lib/fleet";

const SEV = { warning: "#FFCC00", critical: "#FF3B30", info: "#00F0FF" };

export const AlertBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api
      .get("/alerts", { params: { only_unacked: true } })
      .then(({ data }) => setAlerts(data))
      .catch(() => {});
  }, []);

  useEffect(load, [load]);

  useFleetSocket(
    useCallback((msg) => {
      if (msg.type === "alert") setAlerts((prev) => [msg.alert, ...prev].slice(0, 50));
    }, [])
  );

  const ack = async (id) => {
    await api.patch(`/alerts/${id}/ack`);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const ackAll = async () => {
    await api.post("/alerts/ack-all");
    setAlerts([]);
  };

  return (
    <div className="relative">
      <button
        data-testid="alert-bell-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative border border-white/10 p-1.5 text-[#A1A1AA] transition-colors duration-150 hover:border-[#FFCC00] hover:text-[#FFCC00]"
        aria-label="Alerts"
      >
        <Bell size={14} />
        {alerts.length > 0 && (
          <span
            data-testid="alert-unread-count"
            className="absolute -right-2 -top-2 min-w-[16px] border border-[#0A0A0A] bg-[#FF3B30] px-1 text-[9px] font-bold leading-4 text-black"
          >
            {alerts.length}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alert-dropdown"
          className="absolute right-0 top-9 z-[1400] max-h-[60vh] w-[320px] overflow-y-auto border border-white/15 bg-[#0A0A0A] doot-scroll"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <p className="overline">alerts</p>
            {alerts.length > 0 && (
              <button
                data-testid="ack-all-alerts-btn"
                onClick={ackAll}
                className="text-[10px] tracking-[0.15em] text-[#00F0FF] hover:underline"
              >
                ACK ALL
              </button>
            )}
          </div>
          {alerts.length === 0 ? (
            <p className="px-3 py-6 text-xs text-[#52525B]" data-testid="no-alerts">
              NO ACTIVE ALERTS
            </p>
          ) : (
            alerts.map((a) => (
              <div
                key={a.id}
                data-testid="alert-item"
                className="flex items-start gap-2 border-b border-white/5 px-3 py-3 hover:bg-[#141414]"
              >
                <span
                  className="mt-1 inline-block h-1.5 w-1.5 shrink-0"
                  style={{ background: SEV[a.severity] || SEV.info }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] leading-snug">{a.message}</p>
                  <p className="overline mt-1">
                    {a.type} · {sinceLabel(a.created_at)} ago
                  </p>
                </div>
                <button
                  onClick={() => ack(a.id)}
                  data-testid="ack-alert-btn"
                  className="border border-white/10 p-1 text-[#52525B] hover:border-[#00FF66] hover:text-[#00FF66]"
                  aria-label="Acknowledge"
                >
                  <Check size={11} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
